// IMPORT REACT
import React, {
  useContext,
  useEffect,
  useState
} from 'react'

// IMPORT UI COMPONENTS
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  Typography
} from '@mui/material'

import CustomBreadcrumbs from '../../components/BreadcrumbsComponent/CustomBreadcrumbs'
import {
  useLocation,
  useNavigate
} from 'react-router-dom';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Controller,
  FormProvider,
  useForm,
  useWatch
} from 'react-hook-form';
import SkeletonWrapper from '../../components/Skeleton/SkeletonWrapper';
import { CustomTextField } from '../../components/CustomFields/TextField';
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import LoadingButton from '@mui/lab/LoadingButton';
import { useQuery } from '../../shared/CommonFunction';
import { EncryptDecryptService } from '../../services/common/EncryptionService';
import {
  CustomSelect,
  CustomSelectWithoutForm
} from '../../components/CustomFields/CustomSelect';
import { PAGE_SIZE } from '../../services/common/const';
import { PreferencesContext } from '../../PreferenceContext';
import {
  CreateReasonForReference,
  GetReasonForReferenceDetail,
  UpdateReasonForReference
} from '../../services/ReasonForReferenceService';
import { AlertType } from '../../services/models/AlertType';
import {
  GetTeamMemberList,
  GetTeamMemberLocationList
} from '../../services/TeamMemberService';
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";

// SCHEMA FOR PARAMETER
interface Parameter {
  consumerId: string;
  orgId: string;
  branchId: string;
  teamMemberId: Array<any>;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  tabType: number;
  mode: string;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  consumerId: "",
  orgId: "",
  branchId: "",
  teamMemberId: [],
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  tabType: 0,
  mode: "DROPDOWN",
};

interface ILocation {
  locationId: number;
  locationName: string;
  code: string;
  label: string;
  value: string;
}

interface LocationData {
  locationColl: Array<ILocation> | null;
  totalCount: number;
}

const initLocationData: LocationData = {
  locationColl: [],
  totalCount: 0,
}

// SCHEMA FOR DETAIL COLL OF FORM
interface IReasonForReferenceDetail {
  reasonForReferenceDetailId: number;
  displayOrder: number;
  reasonForReferenceName: string;
  isDeleted: boolean;
  mode?: string | null;
}

// SCHEMA FOR FORM
interface IReasonForReference {
  reasonForReferenceId: number;
  consumerId: number;
  organizationId: number;
  branchId: number;
  reasonForReferenceName: string;
  locationId: number;
  locationName: string;
  locationCode: string;
  doctorId: number;
  doctorName: string;
  isOutReach: boolean;
  reasonForReferenceDetailColl: Array<IReasonForReferenceDetail>;
  isDeleted: boolean;
}

//DEFAULT SCHEMA VALUE FOR FORM DETAIL
const initReasonForReferenceDetail: IReasonForReferenceDetail = {
  reasonForReferenceDetailId: 0,
  displayOrder: 0,
  reasonForReferenceName: "",
  isDeleted: false,
  mode: "I",
};

// SCHEMA FOR RESULTDATA
interface ResultData {
  tabTypeColl: Array<any>;
  totalCount: number;
}

// DEFAULT VALUES FOR RESULT DATA
const initResultData: ResultData = {
  tabTypeColl: [],
  totalCount: 0,
};

const ReasonForReferenceSave = () => {

  const navigate = useNavigate();

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);

  const edService = new EncryptDecryptService();
  // const [locationData, setLocationData] = useState<LocationData>(initLocationData);
  const [locationData, setLocationData] = useState<any[]>([]);
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const [doctorData, setDoctorData] = useState<any[]>([]);

  const location = useLocation();

  const selectedDoctor = location.state?.selectedDoctor;

  const [param, setParam] = useState(() => ({
    ...parameters,
    teamMemberId: selectedDoctor,
  }));

  const initReasonForReferenceData: IReasonForReference = {
    reasonForReferenceId: 0,
    consumerId: 0,
    organizationId: 0,
    branchId: 0,
    reasonForReferenceName: "",
    locationId: 0,
    locationName: "",
    locationCode: "",
    doctorId: 0,
    doctorName: "",
    isOutReach: false,
    reasonForReferenceDetailColl: [initReasonForReferenceDetail],
    isDeleted: false,
  };

  // GETTING PARAMS
  let urlParams = useQuery();
  let curReasonForReferenceId = urlParams.get("q");
  let reasonForReferenceId = 0;
  const selectedDoctorId = param.teamMemberId?.value || 0;
  const doctorName = encodeURIComponent(param.teamMemberId?.label || "");

  if (curReasonForReferenceId !== null && curReasonForReferenceId !== undefined) {
    let decId = edService.decrypt(curReasonForReferenceId);
    if (decId !== "" && decId !== null && decId !== undefined) {
      reasonForReferenceId = +decId;
    }
  } else {
    navigate("/not-found");
  }

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    reasonForReferenceId: Yup.number().nullable(),
    // reasonForReferenceName: Yup.string().required("Name is required"),
    doctorName: Yup.string().required("Doctor is required"),
    locationId: Yup.mixed()
      .nullable()
      .test("is-required", "Doctor Location is required", (value) => {
        return value !== null && value !== undefined;
      }),
    locationName: Yup.object()
      .nullable()
      .transform((value) => (value === "" ? null : value))
      .required("Location is required"),
    reasonForReferenceDetailColl: Yup.array()
      .of(
        Yup.object().shape({
          reasonForReferenceDetailId: Yup.number().required(),
          displayOrder: Yup.number().required(),
          reasonForReferenceName: Yup.string().required(),
          isDeleted: Yup.boolean().required(),
          mode: Yup.string().nullable(),
        })
      )
      .required(),
    isDeleted: Yup.boolean(),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: initReasonForReferenceData, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });

  // GET LIST OF DOCTOR
  const fetchDoctorList = async () => {
    try {
      let doctorCollection = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext?.consumerId?.toString());
      params.append("orgId", userContext?.organizationId?.toString());
      params.append("branchId", userContext?.branchId?.toString());
      params.append("isConsultingDoctor", "true");
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);

      const result: any = await GetTeamMemberList(params);

      if (result?.data?.data?.teamMemberColl) {
        doctorCollection = result?.data?.data?.teamMemberColl;
        setDoctorData(doctorCollection);
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  const getLocationByToken = async () => {
    try {
      let params = new URLSearchParams();
      let resultData: any = [];
      const teamMemberId = param?.teamMemberId?.value || 0;

      params.append("consumerId", userContext?.consumerId?.toString());
      params.append("organizationId", userContext?.organizationId?.toString());
      // params.append("branchId", userContext?.branchId?.toString());
      params.append("teamMemberId", teamMemberId.toString());
      params.append("isContainBranch", "true");
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      // params.append("mode", "DROPDOWN");

      const result: any = await GetTeamMemberLocationList(params);
      if (result?.data?.data) {
        resultData = result.data.data.locationColl || [];
        setLocationData(resultData);
      }
      return resultData;
    } catch (e) {
      console.log("err:", e);
    }
  };

  const saveReasonForReference = async (value: IReasonForReference) => {
    try {
      let result: any = "";
      const doctorObj = param.teamMemberId;

      const reasonForReferenceDetailColl = value?.reasonForReferenceDetailColl?.map((x, index) => {
        let col: any = x;
        col.reasonForReferenceDetailId = x.reasonForReferenceDetailId || 0;
        col.displayOrder = x.displayOrder || index + 1;
        if (!col.mode) {
          col.mode = col.reasonForReferenceDetailId > 0 ? "U" : "I";
        }
        return col;
      });

      const reasonForReferenceName = value?.reasonForReferenceDetailColl
        ?.filter(x => !x.isDeleted && x.reasonForReferenceName)
        ?.map(x => x.reasonForReferenceName.trim())
        ?.join(", ") || "";

      let formData = {
        reasonForReferenceId: value?.reasonForReferenceId || 0,
        consumerId: userContext?.consumerId || 0,
        organizationId: userContext?.organizationId || 0,
        branchId: userContext?.branchId || 0,
        reasonForReferenceName: reasonForReferenceName,

        locationId:
          value?.locationName?.locationId ||
          value?.locationName?.value ||
          0,
        locationName:
          value?.locationName?.label ||
          value?.locationName?.locationName ||
          "",
        locationCode:
          value?.locationName?.code ||
          value?.locationCode ||
          "",

        doctorId: doctorObj?.value || 0,
        doctorName: doctorObj?.label || "",

        reasonForReferenceDetail: JSON.stringify(reasonForReferenceDetailColl),
        isDeleted: value.isDeleted,
        dataManagedBy: userContext.loginDetail.userId
      };

      if (reasonForReferenceId > 0) {
        result = await UpdateReasonForReference(formData)
      } else {
        result = await CreateReasonForReference(formData);
      }

      if (result?.data) {
        navigate(-1);
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // GET REASON FOR REFERENCE DETAIL
  const getReasonForReferenceDetailById = async () => {
    let result: any;
    let params = new URLSearchParams();
    params.append("reasonForReferenceId", reasonForReferenceId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString());
    params.append("organizationId", userContext?.organizationId?.toString());
    params.append("branchId", userContext?.branchId?.toString());
    try {
      const res: any = await GetReasonForReferenceDetail(params);
      result = res.data.data;
      methods.reset({
        ...initReasonForReferenceData,
        reasonForReferenceId: result?.reasonForReferenceId || 0,
        reasonForReferenceName: result?.reasonForReferenceName || "",
        doctorName: result?.doctorName,

        locationName: result?.locationId
          ? {
            value: result.locationId,
            label: result.locationName,
            code: result.locationCode
          }
          : null,

        locationId: result?.locationId || 0,
        locationCode: result?.locationCode || "",
        reasonForReferenceDetailColl: result?.reasonForReferenceDetailColl?.filter((x: any) => !x.isDeleted).map((x: any, index: number) => ({
          ...x,
          mode: "U"
        })) || [],
      });

      setParam((prev) => ({
        ...prev,
        teamMemberId: result?.doctorId
          ? {
            value: result.doctorId,
            label: result.doctorName,
          }
          : null,
      }));

      return result;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // HANDLE ADD DETAIL ROW
  const addNewReason = () => {
    const detailColl = methods.getValues().reasonForReferenceDetailColl;

    const maxNumber = detailColl.reduce(
      (acc, curr) => (acc = curr.displayOrder > acc ? curr.displayOrder : acc),
      0
    );

    const newRow: IReasonForReferenceDetail = {
      ...initReasonForReferenceDetail,
      displayOrder: maxNumber + 1,
    };

    methods.reset({
      ...methods.getValues(),
      reasonForReferenceDetailColl: [...detailColl, newRow],
    });
  };

  const handleReasonDetailFieldChange = (
    actualIndex: number,
    field: string,
    value: any
  ) => {
    changeReasonDetailField(actualIndex, field, value);
    methods.trigger(["reasonForReferenceDetailColl"]);
  };

  const changeReasonDetailField = (
    actualIndex: number,
    field: string,
    value: any
  ) => {
    const data = methods.getValues();
    const detailColl = data.reasonForReferenceDetailColl;

    if (actualIndex < 0 || actualIndex >= detailColl.length) return;

    const currRow = detailColl[actualIndex];

    if (field.toLowerCase() === "isdeleted") {
      currRow.isDeleted = true;
      if (currRow.reasonForReferenceDetailId > 0) {
        currRow.mode = "D";
      } else {
        currRow.mode = "I";
      }
    }

    if (field.toLowerCase() === "reasonforreferencename") {
      currRow.reasonForReferenceName = value;
      if (currRow.reasonForReferenceDetailId > 0 && currRow.mode !== "D") {
        currRow.mode = "U";
      }
    }

    methods.reset({
      ...data,
      reasonForReferenceDetailColl: [...detailColl],
    });
  };

  // ON SUBMIT OF FORM
  const onSubmit = (data: any) => {
    saveReasonForReference(data);
  };

  // HANDLE BACK
  const handleBack = () => {
    navigate(-1);
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      await fetchDoctorList();
      await getLocationByToken();
      if (reasonForReferenceId > 0) {
        await getReasonForReferenceDetailById();
        await getLocationByToken();
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  const onError = (errors: any) => {
    console.log("Validation errors:", errors);
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, []);

  useEffect(() => {
    if (param?.teamMemberId?.value > 0) {
      getLocationByToken();
    }
  }, [param.teamMemberId]);

  // HANDLE DELAY FOR SKELETON
  useEffect(() => {
    if (!isLoading) {
      const timeout = setTimeout(() => setShowSkeleton(false), 50); // Delay unmounting the Skeleton
      return () => clearTimeout(timeout);
    } else {
      setShowSkeleton(true);
    }
  }, [isLoading]);

  return (
    <Box>
      <Box className={"main-container"}>
        <Grid container className="page-header">
          <Grid item xs={6} sm={6} md={6}>
            <Box
              sx={{
                display: "flex",
                height: "100%",
                alignItems: "center",
              }}>
              <CustomBreadcrumbs />
            </Box>
          </Grid>
          <Grid item xs={6} sm={6} md={6}>
            <Box className="page-header-icon">
              <Button
                color="primary"
                variant="outlined"
                type="button"
                className="secondary-button"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}>
                Back
              </Button>
            </Box>
          </Grid>
        </Grid>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit, onError)} action="">
            <Grid container gap={2}>
              <Grid item xs={12} mb={3} className="group-container">
                <Grid container item spacing={2}>
                  <Grid item xs={12} sm={6} lg={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text 
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Doctor *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-search-box">
                        <CustomSelectWithoutForm
                          placeholder="Select Doctor"
                          id={"doctorName"}
                          FieldName={"doctorName"}
                          menuPortalTarget={document.querySelector("body")}
                          handleChange={async (e: any) => {
                            setParam({
                              ...param,
                              teamMemberId: e,
                            });
                            methods.setValue("doctorName", e?.label || "", {
                              shouldValidate: true,
                            });
                          }}
                          value={param.teamMemberId}
                          options={doctorData}
                          isClearable
                          isSearchable
                          isNotCreatable
                        // isDisabled
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.doctorName?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} lg={9}></Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.locationName
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Location *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name={"locationName"}
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"locationName"}
                              placeholder="Select Location"
                              FieldName={"locationName"}
                              options={locationData}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              menuPortalTarget={document.querySelector(
                                "body"
                              )}
                              handleChange={async (e: any) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.locationName?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "locationId",
                                    e?.value ? e : 0,
                                    {
                                      shouldValidate: true,
                                    }
                                  );
                                  methods.setValue(
                                    "locationName",
                                    e?.label ? e : null,
                                    {
                                      shouldValidate: true,
                                    }
                                  );
                                  methods.setValue(
                                    "locationCode",
                                    e?.code ? e : null,
                                    {
                                      shouldValidate: true,
                                    }
                                  );
                                  methods.setValue(
                                    "isOutReach",
                                    e?.locationTypeCode === "OUTREACH"
                                      ? true
                                      : false,
                                    {
                                      shouldValidate: true,
                                    }
                                  );
                                }
                              }}
                              className={`text-input ${methods.formState.errors.locationId
                                ? "error"
                                : ""
                                }`}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.locationId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}></Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.reasonForReferenceName
                          ? "validation-message"
                          : ""
                          }`}>
                        Reason For Reference Name *
                      </FormLabel>

                      <Grid container spacing={1}>
                        {methods
                          .getValues()
                          ?.reasonForReferenceDetailColl
                          ?.map(
                            (
                              row: IReasonForReferenceDetail,
                              actualIndex: number,
                              arr: Array<IReasonForReferenceDetail>
                            ) => {
                              if (row.isDeleted) return null;
                              
                              const visibleItems = arr.filter(x => !x.isDeleted);
                              const length = visibleItems.length;
                              
                              return (
                                <Grid
                                  item
                                  xs={12}
                                  key={actualIndex}>
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                      <SkeletonWrapper
                                        isLoading={showSkeleton}
                                        className="skeleton-input">
                                        <CustomTextField
                                          placeholder=""
                                          autoCapitalize="none"
                                          type="text"
                                          fullWidth
                                          id={`reasonForReferenceName-${actualIndex}`}
                                          size="small"
                                          {...methods.register(
                                            `reasonForReferenceDetailColl.${actualIndex}.reasonForReferenceName`
                                          )}
                                          error={
                                            !!methods.formState.errors
                                              ?.reasonForReferenceDetailColl?.[actualIndex]
                                              ?.reasonForReferenceName
                                          }
                                          className={`text-input ${methods.formState.errors
                                            ?.reasonForReferenceDetailColl?.[actualIndex]
                                            ?.reasonForReferenceName
                                            ? "error"
                                            : ""
                                            }`}
                                        />
                                      </SkeletonWrapper>
                                    </Grid>

                                    <Grid item xs={12} sm={1}>
                                      <Box
                                        sx={{
                                          height: "100%",
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        {length > 1 && (
                                          <ClearIcon
                                            onClick={() =>
                                              handleReasonDetailFieldChange(
                                                actualIndex,
                                                "isDeleted",
                                                true
                                              )
                                            }
                                            style={{
                                              color: "red",
                                              cursor: "pointer",
                                            }}
                                          />
                                        )}
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              );
                            }
                          )}

                        <Grid
                          item
                          xs={12}
                          sm={3}
                          sx={{
                            display: "flex",
                            alignItems: "center"
                          }}
                        >
                          <Button
                            variant="text"
                            disabled={isLoading}
                            className="add-btn"
                            onClick={addNewReason}
                          >
                            <AddIcon fontSize="small" />
                            Add New
                          </Button>
                        </Grid>
                      </Grid>
                      <Typography className="validation-message">
                        {methods.formState.errors.reasonForReferenceName?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="contained"
                    type="submit"
                    className="primary-button"
                    name="save"
                    disabled={isLoading}
                  >
                    {reasonForReferenceId > 0 ? "Update" : "Save"}
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    name="cancel"
                    onClick={handleBack}
                  >
                    Cancel
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Box>
    </Box>
  )
}

export default ReasonForReferenceSave
